/**
 * RemotePlayersManager - Manages the rendering and state of remote players
 */
import * as THREE from 'three';
import { BARBARIAN, KNIGHT, MAGE, ROGUE } from '../controllers/Player.js';

export class RemotePlayersManager {
  /**
   * Create a new RemotePlayersManager
   * @param {SceneManager} sceneManager - The scene manager
   * @param {AssetManager} assetManager - The asset manager
   * @param {Socket} socket - The Socket.IO client instance
   */
  constructor(sceneManager, assetManager, socket) {
    this.sceneManager = sceneManager;
    this.assetManager = assetManager;
    this.socket = socket;
    this.remotePlayers = new Map(); // Maps player IDs to their player objects
    this.playerTypes = [BARBARIAN, KNIGHT, MAGE, ROGUE]; // Available player types
    
    // Direct mapping of character types to their specific attack animations
    this.characterAttackAnimations = {
      'BARBARIAN': '2H_Melee_Attack_Spinning',
      'KNIGHT': '2H_Melee_Attack_Chop',
      'MAGE': 'Spellcast_Shoot',
      'ROGUE': '2H_Ranged_Shoot'
    };

    // Setup socket event handlers
    this._setupSocketHandlers();
  }

  /**
   * Set up Socket.IO event handlers
   * @private
   */
  _setupSocketHandlers() {
    if (!this.socket) {
      console.error('Socket not available for RemotePlayersManager');
      return;
    }

    // Receive initial player states when joining
    this.socket.on('initial-states', (states) => {
      console.log('Received initial player states:', states);
      this._processPlayerStates(states);
    });

    // Receive regular player state updates
    this.socket.on('player-states', (states) => {
      this._processPlayerStates(states);
    });

    // Handle player disconnects
    this.socket.on('player-disconnect', (data) => {
      this.removePlayer(data.id);
    });
  }

  /**
   * Process received player states
   * @param {Object} states - Player states object (key: player ID, value: player state)
   * @private
   */
  _processPlayerStates(states) {
    if (!states) return;

    // Process each player state
    Object.entries(states).forEach(([playerId, state]) => {
      // Skip processing our own player
      if (playerId === this.socket.id) {
        // Remove our own player from remote players if it exists
        if (this.remotePlayers.has(playerId)) {
          this.removePlayer(playerId);
        }
        return;
      }

      if (this.remotePlayers.has(playerId)) {
        // Update existing player
        this._updatePlayerPosition(playerId, state);
      } else {
        // Create new player
        this._createRemotePlayer(playerId, state);
      }
    });

    // Check for players that are no longer in the state update
    this.remotePlayers.forEach((player, id) => {
      if (!states[id] || id === this.socket.id) {
        this.removePlayer(id);
      }
    });
  }

  /**
   * Create a new remote player
   * @param {string} playerId - The player's socket ID
   * @param {Object} state - The player's initial state
   * @private
   */
  async _createRemotePlayer(playerId, state) {
    try {
      console.log(`Creating remote player for ${playerId}...`);
      
      // Ensure asset manager is fully initialized
      if (!this.assetManager.models.characters || 
          Object.keys(this.assetManager.models.characters).length === 0) {
        console.warn('Asset manager not fully loaded, delaying remote player creation');
        // Delay creation and retry after a short time
        setTimeout(() => this._createRemotePlayer(playerId, state), 500);
        return;
      }
      
      // Get player class from state if available, otherwise use default method
      let PlayerClass;
      
      if (state.characterType) {
        // Use the character type sent from the server
        console.log(`Remote player ${playerId} specified character type: ${state.characterType}`);
        
        // Find matching player class by name
        PlayerClass = this.playerTypes.find(type => 
          type.name.toLowerCase() === state.characterType.toLowerCase()
        );
        
        // Fallback if no match (shouldn't happen with proper server sync)
        if (!PlayerClass) {
          console.warn(`Character type ${state.characterType} not found, using fallback`);
          PlayerClass = this.playerTypes[0]; // Default to first type
        }
      } else {
        // Legacy method - use player ID hash to select a type
        console.warn(`No character type provided for ${playerId}, using deterministic fallback`);
        const playerTypeIndex = Math.abs(playerId.split('').reduce((acc, char) => {
          return acc + char.charCodeAt(0);
        }, 0)) % this.playerTypes.length;
        
        PlayerClass = this.playerTypes[playerTypeIndex];
      }
      
      console.log(`Selected player class: ${PlayerClass.name} for remote player ${playerId}`);
      
      // Create the player instance
      const player = new PlayerClass(
        this.sceneManager.scene, 
        this.assetManager,
        { 
          name: state.username || 'Guest',
          isRemotePlayer: true // Flag to identify this is a remote player
        }
      );
      
      console.log(`Initializing remote player ${playerId} with model: ${player.modelPath}`);
      
      // Initialize the player model with retry mechanism
      let initSuccess = false;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!initSuccess && retryCount < maxRetries) {
        initSuccess = await player.init();
        
        if (!initSuccess) {
          console.warn(`Failed to initialize remote player ${playerId}, retry ${retryCount + 1}/${maxRetries}`);
          retryCount++;
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (!initSuccess) {
        console.error(`Failed to initialize remote player ${playerId} after ${maxRetries} attempts`);
        return;
      }
      
      console.log(`Remote player ${playerId} initialized successfully`);
      
      // Set initial position and rotation
      if (state.position) {
        player.position.set(
          state.position.x, 
          state.position.y, 
          state.position.z
        );
        player.modelMesh.position.copy(player.position);
      }
      
      if (state.rotation) {
        player.rotation.y = state.rotation.y;
        player.modelMesh.rotation.y = state.rotation.y;
      }
      
      // Store the remote player
      this.remotePlayers.set(playerId, player);
      console.log(`Created remote player: ${playerId}`);
      
      // Make sure the player is in the scene
      if (!this.sceneManager.scene.getObjectById(player.modelMesh.id)) {
        console.log(`Adding remote player model to scene...`);
        this.sceneManager.scene.add(player.modelMesh);
      }
      
      // Explicitly force animation setup
      console.log(`Setting up animations for remote player ${playerId}`);
      if (!player.animation || !player.animation.mixer) {
        player.setAnimation('idle');
      }
      
      // Force animation to play with multiple approaches
      console.log(`Forcing animation to play for remote player ${playerId}`);
      
      // Approach 1: Direct call to playAnimation
      if (player.playAnimation) {
        player.playAnimation('idle');
      }
      
      // Approach 2: Direct access to animation actions
      if (player.animation && player.animation.actions && player.animation.actions.idle) {
        player.animation.actions.idle.reset();
        player.animation.actions.idle.play();
        player.currentAnimation = 'idle';
      }
      
      // Approach 3: Use _playRemoteAnimation helper
      this._playRemoteAnimation(player, 'idle');
      
      // Approach 4: Force immediate animation updates multiple times
      // This is critical to get out of T-pose immediately
      if (player.animation && player.animation.mixer) {
        console.log(`Forcing initial animation updates for remote player ${playerId}`);
        // Multiple updates to ensure animation progresses past the first frame
        for (let i = 0; i < 5; i++) {
          player.animation.mixer.update(0.016);
        }
      }
      
      // Mark player as needing an animation fix on the first frame
      player.needsAnimationFix = true;
      
      // Schedule additional animation fixes for the next few frames
      // This helps resolve the T-pose issue that appears initially
      this._scheduleAnimationFixes(player, playerId);
      
    } catch (error) {
      console.error(`Error creating remote player ${playerId}:`, error);
    }
  }

  /**
   * Schedule multiple animation fixes to run over the next few frames
   * This helps ensure the animation starts playing correctly
   * @param {Object} player - The player object
   * @param {string} playerId - The player's ID
   * @private
   */
  _scheduleAnimationFixes(player, playerId) {
    // Schedule fixes at staggered intervals
    const intervals = [100, 250, 500, 1000, 2000]; // milliseconds
    
    intervals.forEach(delay => {
      setTimeout(() => {
        if (this.remotePlayers.has(playerId)) {
          console.log(`Running scheduled animation fix for ${playerId} at ${delay}ms`);
          
          // Reinitialize animation if needed
          if (!player.animation || !player.animation.mixer) {
            player.setAnimation('idle');
          }
          
          // Play the idle animation
          this._playRemoteAnimation(player, 'idle');
          
          // Force multiple mixer updates
          if (player.animation && player.animation.mixer) {
            for (let i = 0; i < 3; i++) {
              player.animation.mixer.update(0.016);
            }
          }
        }
      }, delay);
    });
  }

  /**
   * Update a remote player's position and animation
   * @param {string} playerId - The player's socket ID
   * @param {Object} state - The player's updated state
   * @private
   */
  _updatePlayerPosition(playerId, state) {
    const player = this.remotePlayers.get(playerId);
    if (!player || !player.modelMesh) return;

    const prevPosition = player.position.clone();
    
    // Initialize position buffer if needed
    if (!player.positionBuffer) {
      player.positionBuffer = [];
      player.maxBufferSize = 3;
    }
    
    // Add new position to buffer
    const newPosition = new THREE.Vector3(
      state.position.x,
      state.position.y,
      state.position.z
    );
    
    player.positionBuffer.push({
      position: newPosition.clone(),
      rotation: state.rotation.y,
      timestamp: Date.now()
    });
    
    // Keep buffer size limited
    if (player.positionBuffer.length > player.maxBufferSize) {
      player.positionBuffer.shift();
    }
    
    // Calculate interpolation factor based on velocity and network conditions
    let interpolationFactor = 0.2; // Base interpolation factor
    
    // If we have velocity data, use it to adjust interpolation
    if (state.velocity) {
      const velocity = new THREE.Vector3(
        state.velocity.x,
        state.velocity.y,
        state.velocity.z
      );
      const speed = velocity.length();
      
      // Adjust interpolation based on speed
      if (speed > 0.1) {
        interpolationFactor = Math.min(0.3, 0.2 + speed * 0.1);
      }
    }
    
    // Smoothly move toward the target position
    player.position.lerp(newPosition, interpolationFactor);
    player.modelMesh.position.copy(player.position);
    
    // Update rotation with improved interpolation
    if (state.rotation) {
      const currentRotation = player.modelMesh.rotation.y;
      let targetRotation = state.rotation.y;
      
      // Handle rotation wraparound
      let rotationDiff = targetRotation - currentRotation;
      if (Math.abs(rotationDiff) > Math.PI) {
        rotationDiff = rotationDiff > 0 
          ? rotationDiff - 2 * Math.PI 
          : rotationDiff + 2 * Math.PI;
      }
      
      // Apply smooth rotation with speed-based interpolation
      const rotationInterpolation = Math.min(0.3, interpolationFactor * 1.5);
      const newRotation = currentRotation + rotationDiff * rotationInterpolation;
      player.modelMesh.rotation.y = newRotation;
      player.rotation.y = newRotation;
    }
    
    // Update animation state
    if (state.isAttacking) {
      // Handle attack animation
      if (!player.isAttacking || player.attackStartTime !== state.attackStartTime) {
        player.isAttacking = true;
        player.attackStartTime = state.attackStartTime;
        this._playRemoteAnimation(player, state.currentAnimation || 'attack');
      }
    } else {
      // Handle movement animation
      const distance = player.position.distanceTo(prevPosition);
      const isMoving = distance > 0.01 || state.isMoving;
      
      if (isMoving) {
        if (player.currentAnimation !== 'run' && player.currentAnimation !== 'running') {
          this._playRemoteAnimation(player, 'run');
        }
      } else {
        if (player.currentAnimation !== 'idle') {
          this._playRemoteAnimation(player, 'idle');
        }
      }
      
      player.isMoving = isMoving;
      player.isAttacking = false;
    }
  }
  
  /**
   * Update all remote players
   * @param {number} deltaTime - Time since last update in seconds
   */
  update(deltaTime) {
    // Ensure deltaTime is reasonable
    if (deltaTime <= 0 || deltaTime > 0.1) {
      deltaTime = 0.016; // Default to ~60fps
    }
    
    // Update animations for remote players
    this.remotePlayers.forEach((player, id) => {
      // Extra updates for players that need animation fixes
      if (player.needsAnimationFix) {
        console.log(`Applying animation fix for player ${id}`);
        this._playRemoteAnimation(player, player.isMoving ? 'run' : 'idle');
        player.needsAnimationFix = false;
      }
      
      // Initialize animation clock if it doesn't exist
      if (!player.animationClock) {
        player.animationClock = {
          time: 0,
          lastUpdate: Date.now() / 1000,
          frameCount: 0
        };
      }
      
      // Update animation clock
      const now = Date.now() / 1000;
      const clockDelta = now - player.animationClock.lastUpdate;
      player.animationClock.time += clockDelta;
      player.animationClock.lastUpdate = now;
      player.animationClock.frameCount++;
      
      // Check if an attack animation has ended based on its duration
      if (player.isAttacking && player.attackAnimationEnd && Date.now() > player.attackAnimationEnd) {
        console.log(`Attack animation timeout for player ${id} (${player.currentAnimation})`);
        this._handleAttackAnimationComplete(player);
      }
      
      // For moving players, apply more aggressive animation fixes
      if (player.isMoving && player.modelMesh) {
        // Force complete leg rotation updates if animation seems stuck
        if (player.animationClock.frameCount % 10 === 0) {  // Every 10 frames
          this._forceLegAnimation(player);
        }
      }
      
      // If this player is moving, ensure the animation loop is working properly
      if (player.animation && player.animation.mixer && player.currentAnimation) {
        const action = player.animation.actions[player.currentAnimation];
        if (action) {
          // For attack animations, make sure we fully play through them
          const isAttackAnimation = 
            player.currentAnimation === '2H_Melee_Attack_Spinning' ||
            player.currentAnimation === 'Spellcast_Shoot' ||
            player.currentAnimation === '2H_Melee_Attack_Chop' ||
            player.currentAnimation === '2H_Ranged_Shoot' ||
            player.currentAnimation.toLowerCase().includes('attack');
           
          if (isAttackAnimation) {
            // For attack animations, manually advance the animation time
            // to ensure it plays smoothly toward completion
            if (typeof action.time !== 'undefined' && action.getClip) {
              // Get the clip duration
              const clipDuration = action.getClip().duration;
              
              // Calculate progress through animation (0 to 1)
              const progress = action.time / clipDuration;
              
              // Advance the animation time based on deltaTime
              // but avoid reaching exactly 1.0 which can cause issues
              const newTime = Math.min(action.time + (deltaTime * 1.2), clipDuration * 0.98);
              action.time = newTime;
              
              // Log progress for debug (but not every frame)
              if (player.animationClock.frameCount % 30 === 0) {
                console.log(`Attack animation progress: ${(progress * 100).toFixed(2)}%`);
              }
              
              // Force the mixer to apply the new time
              player.animation.mixer.update(0);
            }
          }
          // For walking/running animations, continue with the existing cycle update logic
          else if (player.isMoving && 
              (player.currentAnimation === 'run' || 
               player.currentAnimation === 'running' || 
               player.currentAnimation === 'walk' || 
               player.currentAnimation === 'walking')) {
            
            // Get the clip duration
            const clipDuration = action.getClip().duration;
            
            // Calculate animation time by using our independent clock
            // and wrap it to the clip duration for proper looping
            const animationTime = player.animationClock.time % clipDuration;
            
            // Directly set the animation time
            action.time = animationTime;
            
            // Force the mixer to apply the new time
            player.animation.mixer.update(0);
            
            // On every 5th frame, force a more aggressive animation update
            if (player.animationClock.frameCount % 5 === 0) {
              // Apply multiple small updates to ensure animation advances smoothly
              for (let i = 0; i < 3; i++) {
                player.animation.mixer.update(0.016);
              }
            }
          }
          
          // Check if action settings need fixing
          if (player.currentAnimation && !isAttackAnimation) {
            // Fix loop settings for non-attack animations
            if (typeof action.setLoop === 'function') {
              action.setLoop(THREE.LoopRepeat);
            } else if (action.loop !== undefined) {
              action.loop = THREE.LoopRepeat;
            }
          } else if (isAttackAnimation) {
            // Fix loop settings for attack animations
            if (typeof action.setLoop === 'function') {
              action.setLoop(THREE.LoopOnce);
            } else if (action.loop !== undefined) {
              action.loop = THREE.LoopOnce;
            }
          }
          
          // Ensure the animation is playing
          if (action.paused) {
            action.paused = false;
          }
          
          if (action.isRunning && !action.isRunning()) {
            action.play();
          }
        }
      }
      
      // Update the player using the normal method for non-moving, non-attacking players
      if (!player.isMoving && !player.isAttacking) {
        if (player.update) {
          player.update(deltaTime);
        } else if (player.animation && player.animation.mixer) {
          player.animation.mixer.update(deltaTime);
        }
      }
    });
  }

  /**
   * Force leg animation update for a player by directly manipulating bone rotations
   * This is a last-resort approach for when animations are stuck
   * @param {Object} player - The player to update
   * @private
   */
  _forceLegAnimation(player) {
    if (!player.modelMesh) return;
    
    const legBoneNames = [
      'leftleg', 'rightleg', 'leftupleg', 'rightupleg', 
      'leftfoot', 'rightfoot', 'lefttoebase', 'righttoebase',
      'leg_l', 'leg_r', 'foot_l', 'foot_r',
      'mixamorigLeftUpLeg', 'mixamorigRightUpLeg',
      'mixamorigLeftLeg', 'mixamorigRightLeg',
      'mixamorigLeftFoot', 'mixamorigRightFoot'
    ];
    
    // Time value to use for animation (0-1 range, looping)
    const time = (player.animationClock.time * 5) % 1;
    
    // Find leg bones and apply rotation
    player.modelMesh.traverse(object => {
      if (object.isBone || object.type === 'Bone') {
        const lowerName = object.name.toLowerCase();
        
        // Check if this is a leg bone
        const isLegBone = legBoneNames.some(name => lowerName.includes(name));
        
        if (isLegBone) {
          // Get current rotation
          const origRotation = object.rotation.clone();
          
          // Apply oscillating rotation based on time
          if (lowerName.includes('left')) {
            // Left leg bones - sine wave motion
            object.rotation.x = Math.sin(time * Math.PI * 2) * 0.5;
          } else if (lowerName.includes('right')) {
            // Right leg bones - cosine wave motion (opposite phase)
            object.rotation.x = Math.cos(time * Math.PI * 2) * 0.5;
          }
          
          // Apply only if significant change needed
          if (Math.abs(object.rotation.x - origRotation.x) < 0.1) {
            // Original rotation was close to our target, animation might be working
            // Restore original rotation
            object.rotation.copy(origRotation);
          }
        }
      }
    });
  }

  /**
   * Play animation for remote player
   * @param {Object} player - The remote player object
   * @param {string} animationName - The animation to play
   */
  _playRemoteAnimation(player, animationName) {
    if (!player || !player.animation || !player.animation.actions) {
      console.warn('Cannot play animation: Player or animations not ready');
      return;
    }

    console.log(`Attempting to play animation "${animationName}" for remote player`);
    
    // Reset animation clock whenever changing animations
    if (!player.animationClock) {
      player.animationClock = {
        time: 0,
        lastUpdate: Date.now() / 1000,
        frameCount: 0
      };
    } else {
      // Keep the clock running but reset its phase to ensure smooth animation start
      player.animationClock.time = 0;
      player.animationClock.lastUpdate = Date.now() / 1000;
      player.animationClock.frameCount = 0;
    }

    // Check if animation exists directly in the player's actions
    let actionName = animationName;
    if (player.animation.actions[animationName]) {
      console.log(`Found animation "${animationName}" directly in player actions`);
    } else {
      console.log(`Animation "${animationName}" not found directly, trying aliases and fallbacks...`);
      
      // Log available animations for debugging
      const availableAnimations = Object.keys(player.animation.actions);
      console.log(`Available animations: ${availableAnimations.join(', ')}`);
      
      // Try common aliases if the exact name doesn't exist
      const aliases = {
        'run': ['running', 'sprint', 'run_forward'],
        'idle': ['standing', 'idle_default', 'rest'],
        'walk': ['walking', 'walk_forward'],
        'attack': ['swing', 'slash', 'strike'],
        // Add specific attack animation aliases with more variations
        '2H_Melee_Attack_Spinning': ['attack', 'swing', 'spin_attack', 'barbarian_attack', '2h_melee_attack_spinning', 'melee_attack_spinning', 'spinning_attack'],
        'Spellcast_Shoot': ['cast', 'magic', 'spell', 'mage_attack', 'spellcast_shoot', 'spellcast', 'magic_shoot'],
        '2H_Melee_Attack_Chop': ['chop', 'sword_attack', 'knight_attack', '2h_melee_attack_chop', 'melee_attack_chop', 'chop_attack'],
        '2H_Ranged_Shoot': ['shoot', 'bow_attack', 'ranged_attack', 'rogue_attack', '2h_ranged_shoot', 'ranged_shoot', 'bow_shoot']
      };
      
      // Check aliases
      const potentialAliases = aliases[animationName];
      if (potentialAliases) {
        for (const alias of potentialAliases) {
          if (player.animation.actions[alias]) {
            actionName = alias;
            console.log(`Found animation through alias: "${alias}" for requested "${animationName}"`);
            break;
          }
        }
      }
      
      // If we still haven't found a match, try a case-insensitive comparison
      if (!player.animation.actions[actionName]) {
        console.log(`No alias found, trying case-insensitive matching...`);
        const lowerCaseAnimName = animationName.toLowerCase();
        
        for (const key of Object.keys(player.animation.actions)) {
          if (key.toLowerCase() === lowerCaseAnimName) {
            actionName = key;
            console.log(`Found animation through case-insensitive match: "${key}"`);
            break;
          }
        }
      }
      
      // Try partial matching if we still don't have a match
      if (!player.animation.actions[actionName]) {
        console.log(`No exact match found, trying partial matching...`);
        const lowerCaseAnimName = animationName.toLowerCase();
        
        for (const key of Object.keys(player.animation.actions)) {
          if (key.toLowerCase().includes(lowerCaseAnimName) || 
              lowerCaseAnimName.includes(key.toLowerCase())) {
            actionName = key;
            console.log(`Found animation through partial match: "${key}" for "${animationName}"`);
            break;
          }
        }
      }
      
      // Fall back to idle if no match
      if (!player.animation.actions[actionName]) {
        console.warn(`Animation "${animationName}" not found, falling back to idle`);
        actionName = 'idle';
        
        if (!player.animation.actions[actionName]) {
          console.error("Critical animation 'idle' is missing");
          return;
        }
      }
    }

    // Get the animation actions
    const newAction = player.animation.actions[actionName];
    const oldAction = player.currentAnimation ? 
      player.animation.actions[player.currentAnimation] : null;

    // Skip if trying to play the same animation that's already running correctly
    if (oldAction === newAction && player.currentAnimation === actionName && 
        oldAction.isRunning && oldAction.isRunning()) {
      console.log(`Animation "${actionName}" is already playing, skipping`);
      // Just ensure the animation settings are correct
      if (typeof newAction.setEffectiveTimeScale === 'function') {
        newAction.setEffectiveTimeScale(1.0);
      }
      if (typeof newAction.setLoop === 'function') {
        newAction.setLoop(THREE.LoopRepeat);
      } else if (newAction.loop !== undefined) {
        newAction.loop = THREE.LoopRepeat;
      }
      return;
    }

    // Safety check
    if (!newAction || typeof newAction.play !== 'function') {
      console.error(`Invalid action "${actionName}" - cannot play`);
      return;
    }

    // Check if this animation has played before
    const hasPlayedBefore = player._playedAnimations && player._playedAnimations[actionName];
    
    // Track which animations have been played
    if (!player._playedAnimations) {
      player._playedAnimations = {};
    }
    player._playedAnimations[actionName] = true;

    console.log(`Playing animation "${actionName}" for remote player (original request: "${animationName}")`);
    
    // Reset and configure the new animation
    newAction.reset();
    
    // Configure animation based on type
    const isAttackAnimation = 
      actionName === '2H_Melee_Attack_Spinning' ||
      actionName === 'Spellcast_Shoot' ||
      actionName === '2H_Melee_Attack_Chop' ||
      actionName === '2H_Ranged_Shoot' ||
      actionName.toLowerCase().includes('attack') ||
      actionName.toLowerCase().includes('swing') ||
      actionName.toLowerCase().includes('chop') ||
      actionName.toLowerCase().includes('cast') ||
      actionName.toLowerCase().includes('shoot') ||
      actionName.toLowerCase().includes('slash');
      
    if (isAttackAnimation) {
      console.log(`Configuring "${actionName}" as an attack animation`);
      
      // Attack animations should play once and hold the final frame
      newAction.clampWhenFinished = true;
      
      // Set the attack to loop once for attack animations
      if (typeof newAction.setLoop === 'function') {
        newAction.setLoop(THREE.LoopOnce);
      } else if (newAction.loop !== undefined) {
        newAction.loop = THREE.LoopOnce;
      }
      
      // Slightly faster playback for attacks
      if (typeof newAction.setEffectiveTimeScale === 'function') {
        newAction.setEffectiveTimeScale(1.2);
      }
      
      // Store the animation duration for auto-transitioning when complete
      const animationDuration = newAction.getClip ? newAction.getClip().duration : 1.5;
      player.attackAnimationEnd = Date.now() + (animationDuration * 1000);
      console.log(`Attack animation duration: ${animationDuration}s, will end at:`, new Date(player.attackAnimationEnd));
      
      // Set up animation finished event handler if available
      if (typeof newAction.onFinish === 'function' || typeof newAction.addEventListener === 'function') {
        console.log(`Setting up animation completion handler for ${actionName}`);
        try {
          if (typeof newAction.onFinish === 'function') {
            newAction.onFinish = () => {
              this._handleAttackAnimationComplete(player);
            };
          } else if (typeof newAction.addEventListener === 'function') {
            newAction.addEventListener('finished', () => {
              this._handleAttackAnimationComplete(player);
            });
          }
        } catch (error) {
          console.warn('Error setting animation completion handler:', error);
        }
      }
    } else {
      // Regular animations should loop
      newAction.clampWhenFinished = false;
      
      // Ensure looping is properly set for non-attack animations
      if (typeof newAction.setLoop === 'function') {
        newAction.setLoop(THREE.LoopRepeat);
      } else if (newAction.loop !== undefined) {
        newAction.loop = THREE.LoopRepeat;
      }
      
      // Normal speed for regular animations
      if (typeof newAction.setEffectiveTimeScale === 'function') {
        newAction.setEffectiveTimeScale(1.0);
      }
      
      // Clear attack animation end time
      player.attackAnimationEnd = null;
    }
    
    // Reset animation time to start fresh, but use a small offset 
    // to get past the first frame immediately
    if (typeof newAction.time !== 'undefined') {
      // Start at a small offset to skip potential T-pose or first frame
      newAction.time = 0.05;
    }

    // Start the new animation
    newAction.play();

    // Use proper crossfade if available
    if (oldAction && oldAction !== newAction && 
        typeof oldAction.crossFadeTo === 'function' && 
        typeof newAction.crossFadeFrom === 'function') {
      // Use shorter crossfade for attack animations
      const crossfadeDuration = isAttackAnimation ? 0.1 : 0.3;
      oldAction.crossFadeTo(newAction, crossfadeDuration);
    } else if (oldAction && oldAction !== newAction && 
               typeof oldAction.fadeOut === 'function') {
      // Alternative fadeout approach if standard crossfade isn't available
      const fadeDuration = isAttackAnimation ? 0.1 : 0.3;
      oldAction.fadeOut(fadeDuration);
      newAction.fadeIn(fadeDuration);
    }

    // Update the current animation reference
    player.currentAnimation = actionName;
    
    // Force immediate updates to ensure animation starts playing properly
    // Multiple small updates help ensure the animation advances past the first frame
    if (player.animation.mixer) {
      // Use more updates for animations that haven't been played before
      const updateCount = hasPlayedBefore ? 5 : 10;
      console.log(`Forcing ${updateCount} animation updates to ensure animation starts properly`);
      for (let i = 0; i < updateCount; i++) {
        // Gradually increasing update times to ensure we advance through multiple frames
        const updateTime = 0.016 * (i + 1) / 2;
        player.animation.mixer.update(updateTime);
      }
    }
  }
  
  /**
   * Handle attack animation completion
   * @param {Object} player - The remote player
   * @private
   */
  _handleAttackAnimationComplete(player) {
    if (!player) return;
    
    console.log('Attack animation completed naturally');
    
    // Clear the attack state
    player.isAttacking = false;
    player.currentAttackAnimation = null;
    
    // Transition back to appropriate animation
    if (player.isMoving) {
      this._playRemoteAnimation(player, 'run');
    } else {
      this._playRemoteAnimation(player, 'idle');
    }
  }

  /**
   * Remove a remote player
   * @param {string} playerId - The player's socket ID
   */
  removePlayer(playerId) {
    const player = this.remotePlayers.get(playerId);
    if (player) {
      // Dispose player resources
      player.dispose();
      this.remotePlayers.delete(playerId);
      console.log(`Removed remote player: ${playerId}`);
    }
  }

  /**
   * Clean up resources
   */
  dispose() {
    // Dispose all remote players
    this.remotePlayers.forEach(player => {
      player.dispose();
    });
    this.remotePlayers.clear();
    
    // Clean up socket event handlers
    if (this.socket) {
      this.socket.off('initial-states');
      this.socket.off('player-states');
      this.socket.off('player-disconnect');
    }
  }
} 